import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTeamMembers } from '../hooks/useSupabase';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from './ImageWithFallback';
import PlayerModal from './PlayerModal';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight } = FiIcons;

const AcademyPlayerCarousel = ({ category = 'K17', title, isVisible = true }) => {
  const { data: allPlayers, loading } = useTeamMembers(false); // is_staff=false
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [visibleCards, setVisibleCards] = useState(4);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter players by category, exclude staff, and sort
  const players = (allPlayers || [])
    .filter(
      (player) =>
        !player.is_staff &&
        Array.isArray(player.team_categories) &&
        player.team_categories.includes(category) &&
        player.name
    )
    .sort((a, b) => {
      const soA = a.sort_order ?? 0;
      const soB = b.sort_order ?? 0;
      if (soA !== soB) return soA - soB;
      const numA = a.number ?? 999;
      const numB = b.number ?? 999;
      if (numA !== numB) return numA - numB;
      return (a.name || '').localeCompare(b.name || '');
    });

  const hasPlayers = players && players.length > 0;

  // Responsive card count
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setVisibleCards(1);
      } else if (window.innerWidth < 768) {
        setVisibleCards(2);
      } else if (window.innerWidth < 1024) {
        setVisibleCards(3);
      } else {
        setVisibleCards(4);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset slide when category changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [category]);

  // Calculate pagination details (safe to do before returns)
  const effectiveVisibleCards = Math.max(1, Math.min(visibleCards, players.length));
  const totalSlides = hasPlayers ? Math.ceil(players.length / effectiveVisibleCards) : 0;

  // Auto-play logic - MOVED BEFORE EARLY RETURNS to comply with Rules of Hooks
  useEffect(() => {
    if (!isAutoPlaying || totalSlides <= 1 || !isVisible || !hasPlayers) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 3000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides, isVisible, hasPlayers]);

  // --- EARLY RETURNS START HERE ---
  if (!isVisible) return null;

  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Φόρτωση παικτριών ακαδημίας...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPlayers) {
    return null;
  }

  // Calculate visible players for rendering
  const startIndex = currentSlide * effectiveVisibleCards;
  const endIndex = startIndex + effectiveVisibleCards;
  const visiblePlayers = players.slice(startIndex, endIndex);

  const nextSlide = () => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const handlePlayerClick = (player) => {
    setSelectedPlayer(player);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPlayer(null);
  };

  return (
    <div className="py-8 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h3 className="text-2xl font-bold text-gray-900">
            {title || `Παίκτριες Ακαδημίας ${category}`}
          </h3>
        </motion.div>

        <div className="relative">
          {/* Navigation Buttons */}
          {totalSlides > 1 && (
            <>
              <button
                onClick={prevSlide}
                onMouseEnter={() => setIsAutoPlaying(false)}
                onMouseLeave={() => setIsAutoPlaying(true)}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all duration-200 flex items-center justify-center group hover:scale-110"
              >
                <SafeIcon icon={FiChevronLeft} className="w-5 h-5 text-gray-600 group-hover:text-primary-600 transition-colors duration-200" />
              </button>
              <button
                onClick={nextSlide}
                onMouseEnter={() => setIsAutoPlaying(false)}
                onMouseLeave={() => setIsAutoPlaying(true)}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all duration-200 flex items-center justify-center group hover:scale-110"
              >
                <SafeIcon icon={FiChevronRight} className="w-5 h-5 text-gray-600 group-hover:text-primary-600 transition-colors duration-200" />
              </button>
            </>
          )}

          {/* Carousel Container */}
          <div
            className="mx-10"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <div className="overflow-hidden">
              <div className="flex">
                {visiblePlayers.map((player, index) => (
                  <motion.div
                    key={player.id}
                    className="flex-shrink-0 px-2 cursor-pointer"
                    style={{ width: `${100 / effectiveVisibleCards}%` }}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    onClick={() => handlePlayerClick(player)}
                  >
                    <div className="group relative h-full">
                      <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 h-full border border-gray-100">
                        {/* Player Photo */}
                        <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
                          <ImageWithFallback
                            src={player.image_url}
                            imageWidth={640}
                            alt={player.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            // Fallback removed to use DEFAULT_LOGO
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          
                          {player.number && (
                            <div className="absolute top-2 right-2 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                              {player.number}
                            </div>
                          )}
                          
                          <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full text-[10px] font-semibold text-gray-800">
                            {player.position}
                          </div>
                        </div>

                        {/* Player Info */}
                        <div className="p-4">
                          <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors duration-200 truncate">
                            {player.name}
                          </h3>
                          <div className="flex items-center justify-between text-xs text-gray-600 mt-1">
                            <span className="font-medium truncate">{player.position}</span>
                            {player.age && (
                              <span className="bg-gray-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                                {player.age} ετών
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Pagination Dots */}
          {totalSlides > 1 && (
            <div className="flex justify-center mt-6 space-x-1.5">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    index === currentSlide ? 'bg-primary-600 scale-125' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Player Modal */}
        <AnimatePresence>
          {isModalOpen && (
            <PlayerModal
              player={selectedPlayer}
              isOpen={isModalOpen}
              onClose={closeModal}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AcademyPlayerCarousel;